import { useEffect, useState } from 'react';
import { useAccess } from '@/lib/session';
import { fetchActiveAnnouncements, type ActiveAnnouncement } from '@/lib/data';

/* Platform announcements broadcast from /superadmin. Filtered to the caller's
   audience (all | trial | active | suspended | <planTier>) and dismissible
   per-session. Shown stacked under the email-verification banner. */

const STYLE: Record<ActiveAnnouncement['level'], { wrap: string; icon: string; iconColor: string }> = {
  info: { wrap: 'bg-blue-50 border-blue-200 text-blue-800', icon: 'ri-information-line', iconColor: 'text-blue-600' },
  warning: { wrap: 'bg-amber-50 border-amber-200 text-amber-800', icon: 'ri-alert-line', iconColor: 'text-amber-600' },
  critical: { wrap: 'bg-accent-50 border-accent-200 text-accent-800', icon: 'ri-error-warning-line', iconColor: 'text-accent-600' },
};

export default function AnnouncementBanner() {
  const access = useAccess();
  const [rows, setRows] = useState<ActiveAnnouncement[]>([]);
  const [dismissed, setDismissed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let alive = true;
    fetchActiveAnnouncements()
      .then((r) => { if (alive) setRows(r); })
      .catch(() => { /* non-fatal */ });
    return () => { alive = false; };
  }, []);

  const matches = (audience: string): boolean => {
    if (audience === 'all') return true;
    if (audience === access.status) return true;          // trialing/active/...
    if (audience === 'trial' && access.status === 'trialing') return true;
    if (audience === 'active' && access.status === 'active') return true;
    if (audience === access.planTier) return true;        // starter/growth/pro
    if (audience === access.effectiveTier) return true;
    return false;
  };

  const visible = rows.filter((a) => !dismissed[a.id] && matches(a.audience));
  if (!visible.length) return null;

  return (
    <>
      {visible.map((a) => {
        const s = STYLE[a.level] ?? STYLE.info;
        return (
          <div key={a.id} className={`border-b px-4 py-2.5 flex items-start gap-3 text-sm ${s.wrap}`}>
            <i className={`${s.icon} ${s.iconColor} mt-0.5`} />
            <span className="flex-1">
              <span className="font-semibold">{a.title}</span>
              <span className="mx-1.5">·</span>
              {a.body}
            </span>
            <button
              type="button"
              onClick={() => setDismissed((d) => ({ ...d, [a.id]: true }))}
              className="opacity-70 hover:opacity-100 cursor-pointer"
              title="Dismiss"
              aria-label="Dismiss"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        );
      })}
    </>
  );
}
